import { createFormatter, type Formatter } from "@positive/format";
import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { defaultFormatPreferences, type FormatPreferences } from "./preferences";

const FormatPreferencesContext = createContext<FormatPreferences>(defaultFormatPreferences);

export function FormatProvider({ preferences, children }: { preferences: FormatPreferences; children: ReactNode }) {
  return <FormatPreferencesContext.Provider value={preferences}>{children}</FormatPreferencesContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- hook belongs with its provider
export function useFormatPreferences(): FormatPreferences {
  return useContext(FormatPreferencesContext);
}

/**
 * Formatter for the current UI language and format preferences; re-created when either changes. With `showHijri` the
 * business dates it formats carry the Hijri (Umm al-Qura) date in brackets (ADR-0009/D-5: display only, input and
 * storage stay Gregorian).
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook belongs with its provider
export function useFormatter(): Formatter {
  const { i18n } = useTranslation();
  const preferences = useContext(FormatPreferencesContext);
  const language = i18n.language;
  return useMemo(
    () =>
      withHijri(
        createFormatter({
          language,
          digits: preferences.digits,
          negativeStyle: preferences.negativeStyle,
          ...(preferences.region ? { region: preferences.region } : {}),
          ...(preferences.timeZone ? { timeZone: preferences.timeZone } : {}),
        }),
        preferences.showHijri,
      ),
    [
      language,
      preferences.digits,
      preferences.negativeStyle,
      preferences.region,
      preferences.timeZone,
      preferences.showHijri,
    ],
  );
}

/** Adds the Hijri date to the formatter's business dates (a date still being typed stays as typed). */
// eslint-disable-next-line react-refresh/only-export-components -- helper of the hook above
export function withHijri(formatter: Formatter, showHijri: boolean): Formatter {
  if (!showHijri) return formatter;
  return {
    ...formatter,
    date(value, style) {
      const gregorian = formatter.date(value, style);
      const hijri = formatter.hijriDate(value, style);
      return hijri === gregorian ? gregorian : `${gregorian} (${hijri})`;
    },
  };
}

/** Formats a business date, with the Hijri date when the user enabled it (same as `useFormatter().date`). */
// eslint-disable-next-line react-refresh/only-export-components -- hook belongs with its provider
export function useDateDisplay(): (value: string | Date) => string {
  const formatter = useFormatter();
  return (value) => formatter.date(value);
}
